import React, {useCallback, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {BulkSelector, FlexRow, IconTooltip, Link, Table, useTableFunctions} from '@forecast-it/design-system';

import {openReport} from './actions/OpenReport';
import {toggleFavoriteReport} from './actions/ToggleFavoriteReport';
import {duplicateReport} from './actions/DuplicateReport';
import {shareReport} from './actions/share-report/ShareReport';
import {renameReport} from './actions/rename-report/RenameReport';
import {deleteReport} from './actions/delete-report/DeleteReport';
import {transferOwnership} from './actions/transfer-ownership/TransferOwnership';

import {hasPermission} from '../../shared/util/PermissionsUtil';
import {ReportRow} from './ReportTypes';
import {PERMISSION_TYPE} from '../../../Permissions';
import {REPORT_COLUMNS} from './Constants';
import {profilePicRectSrc} from '../../../directApi';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import {createFragmentContainer, graphql} from 'react-relay';
import {bulkDeleteReports} from './actions/delete-report/BulkDeleteReport';
import type {ReportsPage_viewer$data as ReportsPage_viewer} from './__generated__/ReportsPage_viewer.graphql';
import {bulkShareReports} from './actions/share-report/BulkShareReport';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {SortingState} from '@tanstack/table-core';

type ReportTableProps = {
	enableSelection: boolean;
	company: ReportsPage_viewer['company'];
	reports: ReportRow[];
	search: string;
	groupings: string[];
	scrollRef: React.MutableRefObject<null>;
	setFilteredRows: (rows: ReportRow[]) => void;
	groupingColumnHeader: string;
};

const ReportsTable = ({
	enableSelection,
	company,
	reports,
	search,
	groupings,
	scrollRef,
	setFilteredRows,
	groupingColumnHeader,
}: ReportTableProps): React.JSX.Element => {
	const intl = useIntl();
	const history = useHistory();
	const {formatMessage} = intl;
	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	const canCreateReports = hasPermission(PERMISSION_TYPE.INSIGHTS_CREATE);
	const initialSorting = [{id: REPORT_COLUMNS.EDITED, desc: true}];

	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

	const onSelectionChange = useCallback((rows: ReportRow[]) => {
		trackEvent('Standard Report', 'Bulk Selection Changed', {selectionCount: rows.length});
		setSelectedRowIds(rows.map(row => row.id));
	}, []);

	const selectedRows = useMemo(() => reports.filter(report => selectedRowIds.includes(report.id)), [selectedRowIds, reports]);

	const [tableRef, table] = useTableFunctions();

	const hideShareAction = (row: ReportRow): boolean => {
		const canShare = row.reportService && (row.isReportOwner || isAdmin);
		return !canShare;
	};

	// Only the report-service copy is a create the service refuses without the create permission;
	// the legacy duplicate is a company-scoped copy that permission does not gate, so it stays offered.
	const hideDuplicateAction = (row: ReportRow): boolean => !!row.reportService && !canCreateReports;

	// Every remaining option needs ownership or admin, so without those and without duplicate there is
	// nothing left to put in the menu - render no trigger rather than an empty dropdown.
	const hideActionColumn = (row: ReportRow): boolean => hideDuplicateAction(row) && !row.isReportOwner && !isAdmin;

	return (
		<>
			<Table
				data-cy={'reports-table'}
				data={reports}
				searchValue={search}
				grouping={groupings}
				width={'100%'}
				scrollRef={scrollRef}
				initialSorting={initialSorting}
				stickyHeader
				tableRef={tableRef}
				onSelectionChange={enableSelection ? onSelectionChange : undefined}
				onFilteringChange={setFilteredRows}
				onSortingChange={(sorting: SortingState) => {
					trackEvent('Standard Report', 'Sorting Changed', {sorting});
				}}
				getRowId={(row: ReportRow) => row.id}
				autoResetExpanded={false}
				highlightSearch={true}
			>
				<Table.GroupingColumn header={groupingColumnHeader} defaultColumnKey={'name'} allowSorting>
					<Table.TextColumn
						id={REPORT_COLUMNS.NAME}
						accessorKey={'name'}
						data-cy={'report-name'}
						header={formatMessage({id: 'report.column.name'})}
						allowSorting
						ellipsis
						icon={report => (
							<FlexRow>
								{report.isShared ? (
									<IconTooltip icon={'shared'}>
										{`${formatMessage({id: 'report.shared_with_externals'})}. `}
										<Link onClick={() => shareReport(report)}>See more.</Link>
									</IconTooltip>
								) : report.isSharedWithViewer ? (
									// A recipient reaches the report through their own share and may not be able to
									// read its share list, so the row would otherwise carry no icon at all. No "See
									// more" here: the dialog it opens is the share list they cannot read.
									<IconTooltip icon={'shared'}>{formatMessage({id: 'report.shared_with_you'})}</IconTooltip>
								) : null}
								{!report.privateAccess ? (
									<IconTooltip icon={'internalTime'}>
										{`${formatMessage({id: 'report.company_access'})}. `}
										{report.reportService ? (
											<Link onClick={() => shareReport(report)}>See more.</Link>
										) : null}
									</IconTooltip>
								) : null}
							</FlexRow>
						)}
						alignIconRight
						onClick={(row: ReportRow) => () => openReport(row, history)}
						width={'55%'}
					/>
				</Table.GroupingColumn>
				<Table.TextColumn
					id={REPORT_COLUMNS.TYPE}
					accessorKey={'type'}
					data-cy={'report-type'}
					header={formatMessage({id: 'report.column.type'})}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.DateColumn
					id={REPORT_COLUMNS.EDITED}
					accessorKey={'updatedAt'}
					data-cy={'report-edited'}
					header={formatMessage({id: 'common.edited'})}
					allowSorting
					width={'10%'}
					enableGlobalFilter={false}
				/>
				<Table.AvatarColumn
					id={REPORT_COLUMNS.CREATED_BY}
					accessorKey={'person.fullName'}
					image={row => (row.person?.profilePictureId ? profilePicRectSrc(row.person?.profilePictureId) : undefined)}
					data-cy={'report-createdBy'}
					header={formatMessage({id: 'report.column.createdBy'})}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.DateColumn
					id={REPORT_COLUMNS.LAST_VIEWED_DATE}
					accessorKey={'lastViewed'}
					data-cy={'report-lastViewed'}
					header={formatMessage({id: 'report.column.lastViewed'})}
					allowSorting
					width={'10%'}
					enableGlobalFilter={false}
				/>
				<Table.AvatarColumn
					id={REPORT_COLUMNS.LAST_VIEWED}
					accessorKey={'lastViewedBy.fullName'}
					image={row =>
						row.lastViewedBy?.profilePictureId ? profilePicRectSrc(row.lastViewedBy?.profilePictureId) : undefined
					}
					data-cy={'report-lastViewedBy'}
					header={formatMessage({id: 'report.column.lastViewedBy'})}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.IconColumn
					id={REPORT_COLUMNS.FAVORITES}
					accessorKey={'favorite'}
					data-cy={'report-favorites'}
					header={formatMessage({id: 'report.column.favorites'})}
					align={Table.Align.left}
					allowSorting
					onClick={toggleFavoriteReport}
					icon={(favorite?: boolean) => {
						if (favorite === true) {
							return {
								icon: 'starFill',
								color: 'warning',
							};
						} else if (favorite === false) {
							return {
								icon: 'star',
							};
						}
					}}
					width={'5%'}
				/>
				<Table.TextColumn
					id={REPORT_COLUMNS.ACCESS}
					accessorKey={'access'}
					data-cy={'report-access'}
					header={formatMessage({id: 'report.column.privateAccess'})}
					width={'10%'}
					enableGlobalFilter={false}
					visible={false}
				/>
				<Table.ActionColumn header={''} hidden={hideActionColumn}>
					<Table.ActionColumn.Option
						data-cy={'action-duplicate'}
						onClick={row => {
							trackEvent('Standard Report', 'Menu Action Duplicate');
							duplicateReport(
								row,
								company?.id,
								result => {
									const report = result.duplicateSavedReport.savedReport.node;
									createToast({
										duration: 5000,
										message: formatMessage({id: 'report.has-been-duplicated'}, {name: row.name}),
									});
									table.scrollToId(report.id);
								},
								(transaction: unknown, onFailure: (transaction: unknown) => unknown) => {
									// Everything the standard handling recognises stays with it: an expired
									// session, a missing report, a stated refusal, and a transient network
									// error it retries. Only a failure it would collapse into the generic
									// error modal becomes the toast.
									if (!String(transaction).includes('Internal Error:')) {
										onFailure(transaction);
										return;
									}
									createToast({
										duration: 5000,
										message: formatMessage({id: 'report.duplicate-failed'}, {name: row.name}),
									});
								}
							);
						}}
						hidden={hideDuplicateAction}
					>
						<FormattedMessage id={'common.duplicate'} />
					</Table.ActionColumn.Option>
					<Table.ActionColumn.Option
						data-cy={'action-share'}
						onClick={row => {
							trackEvent('Standard Report', 'Menu Action Share');
							shareReport(row);
						}}
						hidden={hideShareAction}
					>
						<FormattedMessage id={'share_insight.button.share'} />
					</Table.ActionColumn.Option>
					<Table.ActionColumn.Option
						data-cy={'action-rename'}
						onClick={row => {
							trackEvent('Standard Report', 'Menu Action Rename');
							renameReport(row, result => {
								const report = result.renameSavedReport.savedReport;
								createToast({
									duration: 5000,
									message: formatMessage(
										{id: 'save_report.report_renamed'},
										{oldName: row.name, newName: report.name}
									),
								});
								table.scrollToId(report.id);
							});
						}}
						hidden={row => !row.isReportOwner && !isAdmin}
					>
						<FormattedMessage id={'common.rename'} />
					</Table.ActionColumn.Option>
					<Table.ActionColumn.Option
						data-cy={'action-transfer-ownership'}
						onClick={row => {
							trackEvent('Standard Report', 'Menu Action Transfer Ownership');
							transferOwnership(row, intl);
						}}
						hidden={row => !row.reportService || (!row.isReportOwner && !isAdmin)}
					>
						<FormattedMessage id={'common.action.transfer_ownership'} />
					</Table.ActionColumn.Option>
					<Table.ActionColumn.Option
						data-cy={'action-delete'}
						onClick={row => {
							trackEvent('Standard Report', 'Menu Action Delete');
							deleteReport(row, company?.id, intl);
						}}
						hidden={row => !row.isReportOwner && !isAdmin}
					>
						<FormattedMessage id={'common.delete'} />
					</Table.ActionColumn.Option>
				</Table.ActionColumn>
			</Table>
			<BulkSelector
				label={formatMessage({id: 'report.bulk_selector_label'}, {count: selectedRows.length})}
				onClear={table.clearSelections}
				show={selectedRows.length > 0}
				data-cy={'bulk-selector'}
			>
				<BulkSelector.MainAction
					onClick={() =>
						bulkShareReports(selectedRows, () => {
							table.clearSelections();
						})
					}
					data-cy={'bulk-share-reports'}
				>
					{formatMessage({id: 'share_insight.button.share'})}
				</BulkSelector.MainAction>
				<BulkSelector.MainAction
					onClick={() =>
						bulkDeleteReports(selectedRows, company?.id, intl, () => {
							table.clearSelections();
						})
					}
					data-cy={'bulk-delete-reports'}
				>
					{formatMessage({id: 'common.delete'})}
				</BulkSelector.MainAction>
			</BulkSelector>
		</>
	);
};

export default createFragmentContainer(ReportsTable, {
	company: graphql`
		fragment ReportsTable_company on Company {
			id
		}
	`,
});
